import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { shopApi } from '@wa/shared';
import { ProductEditor } from './ProductEditor';
import { Card, Notice, input, primary, rupees, secondary } from './ui';

/**
 * The product list and, when one is open, its editor. Search is server-side so a
 * client with three thousand SKUs gets the same screen as one with thirty.
 */
export function ProductsPanel() {
  const [q, setQ] = useState('');
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState<number | 'new' | null>(null);
  const take = 50;

  const categories = useQuery({ queryKey: ['shop', 'categories'], queryFn: shopApi.categories });
  const products = useQuery({
    queryKey: ['shop', 'products', q, categoryId, showInactive, page],
    queryFn: () => shopApi.products({ q, categoryId, active: showInactive ? null : true, skip: page * take, take }),
  });

  if (open !== null) {
    return <ProductEditor productId={open === 'new' ? null : open} onClose={() => setOpen(null)} />;
  }

  const total = products.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / take));

  return (
    <div className="flex flex-col gap-4">
      <Card title="Products" blurb="What the page lists. Each product has at least one variant — that is what customers put in the cart and what prices attach to."
            actions={<button type="button" className={primary} onClick={() => setOpen('new')}>Add a product</button>}>
        <div className="flex flex-wrap items-center gap-2">
          <input className={`${input} max-w-xs`} placeholder="Search name or SKU" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} />
          <select className={`${input} max-w-48`} value={categoryId ?? ''} onChange={(e) => { setCategoryId(e.target.value ? Number(e.target.value) : null); setPage(0); }}>
            <option value="">All categories</option>
            {categories.data?.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.products})</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-small text-(--color-muted)">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="accent-(--color-brand)" /> include hidden
          </label>
          <span className="ml-auto text-micro text-(--color-muted)">{total.toLocaleString('en-IN')} products</span>
        </div>

        {products.isLoading && <Notice kind="muted">Loading…</Notice>}
        {products.data && products.data.items.length === 0 && (
          <Notice kind="muted">{q || categoryId ? 'Nothing matches.' : 'No products yet. Add one, or import a spreadsheet from the Import tab.'}</Notice>
        )}

        <ul className="divide-y divide-(--color-line-dim)">
          {products.data?.items.map((p) => (
            <li key={p.id}>
              <button type="button" onClick={() => setOpen(p.id)} className="tap flex w-full items-center gap-3 py-2 text-left hover:bg-(--color-sunken)">
                {p.firstImage
                  ? <img src={p.firstImage} alt="" className="size-10 shrink-0 rounded object-cover" />
                  : <span className="size-10 shrink-0 rounded bg-(--color-sunken)" />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-small font-medium">{p.name}{!p.isActive && <span className="ml-2 text-micro text-(--color-muted)">hidden</span>}</span>
                  <span className="block truncate text-micro text-(--color-muted)">
                    {[p.categoryName, p.sku, `${p.variants} variant${p.variants === 1 ? '' : 's'}`, p.stock !== null ? `${p.stock} in stock` : null].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className="shrink-0 tabular-nums text-small">{rupees(p.listPrice)}</span>
              </button>
            </li>
          ))}
        </ul>

        {pages > 1 && (
          <div className="flex items-center justify-end gap-2 text-small">
            <button type="button" className={secondary} disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button>
            <span className="text-(--color-muted)">{page + 1} / {pages}</span>
            <button type="button" className={secondary} disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </Card>
    </div>
  );
}
